import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';

import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function SiteContent() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'CEDOMCA | Editar Site';
  }, []);

  // ─── Styles ──────────────────────────────────────────────────────────────────

  const cardStyle = {
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #e0e0e0',
    boxShadow: 'none',
    borderRadius: 2,
    backgroundColor: '#fff',
  };

  const subItem = (label, color) => (
    <Stack key={label} direction="row" spacing={1} alignItems="center">
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Stack>
  );

  return (
    <Box sx={{ backgroundColor: '#f0f0f0', minHeight: '100vh', padding: '1.5rem' }}>

      {/* ─── Cabeçalho ─── */}
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
        Edição de Conteúdo
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Selecione qual seção do site você deseja editar. Todas as alterações serão refletidas nas páginas públicas do CEDOMCA.
      </Typography>

      {/* ─── Banner informativo ─── */}
      <Paper sx={{ bgcolor: '#e3f2fd', border: '1px solid #90caf9', p: 2, mb: 3, borderRadius: 2, boxShadow: 'none' }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <InfoOutlinedIcon sx={{ color: '#1565c0', mt: 0.25, flexShrink: 0 }} />
          <Box>
            <Typography variant="subtitle2" fontWeight={700} color="#1565c0" sx={{ mb: 0.25 }}>
              Edite o conteúdo sem programar
            </Typography>
            <Typography variant="body2" color="#1565c0">
              Esta interface permite que você atualize textos, imagens e informações das páginas públicas do site de forma simples e intuitiva, sem necessidade de conhecimento técnico.
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Grid container spacing={3}>

        {/* ─── Card: Página Acervo ─── */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Paper sx={cardStyle}>
              <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
                <Box sx={{ bgcolor: '#e3f2fd', borderRadius: 2, p: 1, display: 'inline-flex', mb: 1.5 }}>
                  <MenuBookOutlinedIcon sx={{ color: '#1565c0', fontSize: 28 }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={700}>Página Acervo</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Texto e história institucional exibidos na página do acervo.
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ px: 2.5, py: 2, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Itens editáveis
                </Typography>
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {['Texto de apresentação do acervo', 'História da instituição'].map((label) => subItem(label, '#1565c0'))}
                </Stack>
              </Box>
              <Divider />
              <Box sx={{ px: 2, py: 1 }}>
                <Button size="small" startIcon={<EditIcon />} onClick={() => navigate('/site-content/acervo')} sx={{ textTransform: 'none', color: '#1565c0', fontWeight: 600 }}>
                  Editar seção ›
                </Button>
              </Box>
            </Paper>
          </Box>
        </Grid>

        {/* ─── Card: Página Projetos ─── */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Paper sx={cardStyle}>
              <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
                <Box sx={{ bgcolor: '#e8f5e9', borderRadius: 2, p: 1, display: 'inline-flex', mb: 1.5 }}>
                  <AccountTreeOutlinedIcon sx={{ color: '#2e7d32', fontSize: 28 }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={700}>Página Projetos</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Projetos e iniciativas institucionais em destaque no site.
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ px: 2.5, py: 2, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Itens editáveis
                </Typography>
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {['Lista de projetos em destaque', 'Título e descrição de cada projeto', 'Link para o projeto'].map((label) => subItem(label, '#2e7d32'))}
                </Stack>
              </Box>
              <Divider />
              <Box sx={{ px: 2, py: 1 }}>
                <Button size="small" startIcon={<EditIcon />} onClick={() => navigate('/site-content/projects')} sx={{ textTransform: 'none', color: '#2e7d32', fontWeight: 600 }}>
                  Editar seção ›
                </Button>
              </Box>
            </Paper>
          </Box>
        </Grid>

        {/* ─── Card: Página Contato ─── */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Paper sx={cardStyle}>
              <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
                <Box sx={{ bgcolor: '#fff3e0', borderRadius: 2, p: 1, display: 'inline-flex', mb: 1.5 }}>
                  <PeopleOutlinedIcon sx={{ color: '#e65100', fontSize: 28 }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={700}>Página Contato</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Informações de contato e responsáveis institucionais.
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ px: 2.5, py: 2, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Itens editáveis
                </Typography>
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {['Lista de contatos institucionais', 'Nome e cargo do responsável', 'E-mail e telefone de contato'].map((label) => subItem(label, '#e65100'))}
                </Stack>
              </Box>
              <Divider />
              <Box sx={{ px: 2, py: 1 }}>
                <Button size="small" startIcon={<EditIcon />} onClick={() => navigate('/site-content/contacts')} sx={{ textTransform: 'none', color: '#e65100', fontWeight: 600 }}>
                  Editar seção ›
                </Button>
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
